// Client-side helpers for the parental-consent flow.
//
// Sends the consent invitation through the parental-consent-email Edge
// Function (which fans out to Resend). The function is also responsible
// for logging the approval URL to its logs when RESEND_API_KEY is unset,
// so dev work doesn't need a real mailbox.

import { supabase } from "./supabase";
import { retryWithBackoff } from "./network";

export interface ParentalConsentEmailPayload {
  playerEmail: string;
  parentEmail: string;
  parentName: string;
  /** Optional.
   *
   *  The signup / onboarding callers generate the token locally and are
   *  writing it to the profile in the same breath, so they pass it and the
   *  function verifies it matches the stored value.
   *
   *  The resend path (AccountStatusBanner) omits it: profiles
   *  .parental_consent_token is no longer readable by the client, so there
   *  is nothing to echo back. The function reads the stored token itself,
   *  keyed off the caller's JWT — which is what actually proved ownership
   *  all along. */
  consentToken?: string;
}

/**
 * Trigger the consent email. Resolves with `{ ok: boolean }` so callers can
 * decide whether to surface a "couldn't send — try resend" toast. We never
 * throw: a failed send must not block account creation.
 */
export async function sendParentalConsentEmail(
  payload: ParentalConsentEmailPayload
): Promise<{ ok: boolean; error?: string }> {
  try {
    const { data, error } = await retryWithBackoff(() =>
      supabase.functions.invoke("parental-consent-email", { body: payload })
    );
    if (error) return { ok: false, error: error.message };
    if (data && typeof data === "object" && "error" in data) {
      const msg = String((data as { error: unknown }).error ?? "Unknown error");
      return { ok: false, error: msg };
    }
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : "Network error" };
  }
}

/** Build the URL the parent clicks in the email. Mirrors the route in App.tsx. */
export function buildConsentUrl(consentToken: string): string {
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "";
  return `${origin}/auth/parental-consent?token=${encodeURIComponent(consentToken)}`;
}
